/**
 * localIps.js
 *
 * Retrieves the client's LOCAL/PRIVATE IPv4 addresses via a WebRTC ICE
 * candidate leak. This complements the PUBLIC IP that the backend
 * captures from the X-Forwarded-For header — together they let the
 * admin uniquely identify the home network vs the public egress.
 *
 * ⚠️ LIMITATIONS:
 *   - Modern Chrome / Safari obfuscate private IPs via mDNS (`.local`
 *     candidates instead of real 192.168.x.x). We still capture them —
 *     the mDNS hostname is stable per device so it's still useful.
 *   - Some corporate VPNs override WebRTC to only return the VPN IP.
 *   - Firefox in strict privacy mode returns nothing.
 *
 * Returns an array (deduped, up to 8 entries).
 */
export const getLocalIPs = () =>
  new Promise((resolve) => {
    if (typeof window === "undefined" || typeof RTCPeerConnection === "undefined") {
      resolve([]);
      return;
    }

    const found = new Set();
    const ipRe = /(?:\d{1,3}\.){3}\d{1,3}|[a-f0-9]{4,}(?:-[a-f0-9]{2,}){2,}\.local/gi;
    let pc;

    try {
      pc = new RTCPeerConnection({
        iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
      });
    } catch {
      resolve([]);
      return;
    }

    pc.createDataChannel("ip-probe");
    pc.onicecandidate = (ev) => {
      if (!ev.candidate) return;
      const line = ev.candidate.candidate || "";
      const matches = line.match(ipRe) || [];
      matches.forEach((m) => {
        // Skip the public STUN reflex; the server captures that from XFF.
        // Keep private ranges + mDNS obfuscated addresses.
        if (
          m.endsWith(".local") ||
          m.startsWith("10.") ||
          m.startsWith("192.168.") ||
          /^172\.(1[6-9]|2\d|3[0-1])\./.test(m) ||
          /^169\.254\./.test(m)
        ) {
          found.add(m);
        }
      });
    };

    pc.createOffer()
      .then((offer) => pc.setLocalDescription(offer))
      .catch(() => { /* ignore — some browsers will still fire candidates */ });

    // Give it up to 1.2s to collect candidates
    setTimeout(() => {
      try { pc.close(); } catch { /* noop */ }
      resolve(Array.from(found).slice(0, 8));
    }, 1200);
  });
